import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { useIdentityVerification } from '../hooks/useIdentityVerification';
import FormField from '../components/FormField/FormField';
import AlertBanner from '../components/AlertBanner/AlertBanner';
import CaptchaWidget, { type CaptchaWidgetRef } from '../components/CaptchaWidget/CaptchaWidget';
import { validateEIN, validateZIPCode, validateCaptcha, formatEIN } from '../utils/validators';
import acaLogo from '../assets/images/acalogo.png';
import '../assets/styles/IdentityVerification.css';

interface FieldErrors {
  ein:     string | null;
  zipCode: string | null;
  captcha: string | null;
}

const EMPTY_ERRORS: FieldErrors = { ein: null, zipCode: null, captcha: null };

export default function IdentityVerification() {
  const navigate        = useNavigate();
  const { jwtToken }    = useSession();
  const captchaRef      = useRef<CaptchaWidgetRef>(null);
  const { isLoading, errorCode, errorMessage, submit } = useIdentityVerification();

  const [ein,            setEin]            = useState('');
  const [zipCode,        setZipCode]        = useState('');
  const [captchaId,      setCaptchaId]      = useState('');
  const [enteredCaptcha, setEnteredCaptcha] = useState('');
  const [fieldErrors,    setFieldErrors]    = useState<FieldErrors>(EMPTY_ERRORS);

  useEffect(() => {
    if (!jwtToken) {
      navigate('/error?reason=NO_TOKEN_PROVIDED', { replace: true });
    }
  }, [jwtToken, navigate]);

  if (!jwtToken) return null;

  const validateField = (field: keyof FieldErrors) => {
    setFieldErrors(prev => ({
      ...prev,
      ...(field === 'ein'     && { ein:     validateEIN(ein) }),
      ...(field === 'zipCode' && { zipCode: validateZIPCode(zipCode) }),
      ...(field === 'captcha' && { captcha: validateCaptcha(enteredCaptcha) }),
    }));
  };

  const validateAll = (): boolean => {
    const errors: FieldErrors = {
      ein:     validateEIN(ein),
      zipCode: validateZIPCode(zipCode),
      captcha: validateCaptcha(enteredCaptcha),
    };
    setFieldErrors(errors);
    return Object.values(errors).every(e => e === null);
  };

  const handleCaptchaChange = (id: string, entered: string) => {
    setCaptchaId(id);
    setEnteredCaptcha(entered);
    if (entered) setFieldErrors(e => ({ ...e, captcha: null }));
  };

  const handleZipCodeChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
    setZipCode(digitsOnly);
    if (digitsOnly) setFieldErrors(e => ({ ...e, zipCode: null }));
  };

  // Handle EIN input with automatic formatting (XX-XXXXXXX)
  const handleEinChange = (value: string) => {
    // Format the input automatically as user types
    const formatted = formatEIN(value);
    setEin(formatted);
    // Clear error when user starts correcting the field
    if (formatted) {
      setFieldErrors(e => ({ ...e, ein: null }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    /* console.log('🚀 Form submission started');
    console.log('  EIN:', ein);
    console.log('  ZIP Code:', zipCode);
    console.log('  CAPTCHA ID:', captchaId);
    console.log('  Entered CAPTCHA:', enteredCaptcha); */

    try {
      const isValid = validateAll();
      /* console.log('  Validation passed:', isValid);
      console.log('  Field errors:', JSON.stringify(fieldErrors)); */

      if (!isValid) {
        /* console.log('❌ Validation failed - submission cancelled'); */
        return;
      }

      /* console.log('✅ Validation passed - calling submit()'); */
      submit(
        { ein, zipCode, captchaId, enteredCaptcha },
        (newId, newImage) => captchaRef.current?.loadCaptcha(newId, newImage)
      );
    } catch (error) {
      /* console.error('❌ Error in handleSubmit:', error); */
      throw error;
    }
  };

  return (
    <div className="iv-wrapper">
      <div className="iv-card">
        <div className="iv-header">
          <span className="iv-icon" aria-hidden="true">&#128274;</span>
          <div>
            <h1 className="iv-title">Access your 1095 Form securely online</h1>
            <p className="iv-subtitle">
             {/* For your security, please verify your identity to access your ACA 1095 form(s). */}
              Enter the information below to access your form.
            </p>
          </div>
        </div>

        <p className="iv-required-note">
          Fields marked with <strong>*</strong> are required.
        </p>

        {errorMessage && <AlertBanner message={errorMessage} type="error" />}

        <form onSubmit={handleSubmit} noValidate className="iv-form">
          <div className="iv-fields-container">
            <div className="iv-fields-group">
              <FormField
                id="ein"
                label="EIN (Employer Identification Number)"
                value={ein}
                onChange={handleEinChange}
                onBlur={() => validateField('ein')}
                error={fieldErrors.ein}
                placeholder="e.g., 12-3456789"
                maxLength={12}
                required
                disabled={isLoading}
                infoText="Enter your 9-digit Employer Identification Number (XX-XXXXXXX format)"

              />
              <FormField
                id="zipCode"
                label="Employer ZIP Code"
                value={zipCode}
                onChange={handleZipCodeChange}
                onBlur={() => validateField('zipCode')}
                error={fieldErrors.zipCode}
                placeholder="e.g., 60457"
                maxLength={5}
                required
                disabled={isLoading}
                infoText="Enter the 5-digit ZIP code of your employer address"
              />
            </div>

            <div className="iv-logo-section">
              <img src={acaLogo} alt="ACA Track" className="iv-logo" />
            </div>
          </div>

          <div className="iv-captcha-group">
            <label className="form-field__label">
              CAPTCHA Verification <span className="form-field__required" aria-hidden="true">*</span>
            </label>
            <CaptchaWidget
              ref={captchaRef}
              jwtToken={jwtToken}
              onChange={handleCaptchaChange}
              error={fieldErrors.captcha}
              disabled={isLoading}
            />
          </div>

          {/* <p className="iv-security-note">
            Your information is secure and will only be used to verify your identity.
          </p> */}

          <div className="iv-actions">
           {/*  <button
              type="button"
              className="iv-btn iv-btn--cancel"
              onClick={() => navigate('/error?reason=SCREEN_CANCEL')}
              disabled={isLoading}
            >
              Cancel
            </button> */}
            <button
              type="submit"
              className="iv-btn iv-btn--submit"
              disabled={isLoading || errorCode === 'RATE_LIMIT_EXCEEDED' || errorCode === 'ATTEMPT_FAILURE'}
            >
              {isLoading
                ? ( <><span>Verifying  </span><span className="iv-spinner" aria-label="Verifying…" /></> )
                : 'Verify & Continue ▶'}
            </button>
          </div>

          <p className="iv-disclaimer">
            This computer system is the property of ACA TRACK. It is for authorized use only.
            By using this system, all users acknowledge notice of, and agree to comply with,
            ACA TRACK's Privacy Policy. Unauthorized access or improper use of this system may
            result in administrative disciplinary action, civil and criminal penalties. Any or
            all uses of this system and all data on this system may be intercepted, monitored,
            recorded, copied, audited, inspected, and disclosed to authorized administrators.
            By using this system, the user consents to such disclosure at the discretion of
            authorized personnel. By continuing to use this system you indicate your awareness
            of and consent to these terms and conditions of use.
          </p>
        </form>
      </div>
    </div>
  );
}
